import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  Menu,
  AppBar,
  Select,
  Toolbar,
  Tooltip,
  ListItem,
  MenuItem,
  Container,
  IconButton,
  Typography,
  FormControl,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../../assets/ML_logo_medexp.png';
// import logo from '../../assets/ML_logo_medexp_NY_tree.png';
import routes from '../../helpers/routes.json';
import { logout } from '../../services/auth';
import { createErrorNotification } from '../../utils/createErrorNotification';
import { languageOption } from '../../helpers/options';
import { useAuth, authStatus } from '../../context/auth/AuthContext';
import './Header.css';

const Header = () => {
  const [currentLng, setCurrentLng] = useState(
    () => localStorage.getItem('lngClassifier') || 'ru',
  );
  const [anchorElNav, setAnchorElNav] = useState(null);

  const { status, checkAuth, user } = useAuth();

  const { t, i18n } = useTranslation(['common', 'promptbar']);

  const handleLngChange = e => {
    const { value } = e.target;

    setCurrentLng(value);
    i18n.changeLanguage(value);
    localStorage.setItem('lngClassifier', value);
  };

  const handleOpenNavMenu = event => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      await checkAuth();
    } catch (e) {
      console.error(e);
      createErrorNotification();
    }
  };

  return (
    <AppBar position="fixed">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {status === authStatus.AUTHENTICATED && (
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                color="inherit"
                aria-haspopup="true"
                aria-controls="menu-appbar"
                aria-label="account of current user"
                onClick={handleOpenNavMenu}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {Object.keys(routes).map(route => {
                  if (route === 'settings' && !user?.is_superuser) {
                    return null;
                  }

                  // --- USER ---
                  if (route === 'user' && user?.is_superuser) {
                    return null;
                  }

                  return (
                    <MenuItem key={route} onClick={handleCloseNavMenu}>
                      <Typography
                        noWrap
                        component={NavLink}
                        to={routes[route].primary}
                        className="menu-nav_link"
                      >
                        {t(`common:pages.${route}.primary`)}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          )}

          <Box
            sx={{
              mr: 2,
            }}
            className="logo__wrapper"
          >
            <Typography noWrap component={Link} to="/">
              {/* <img src={logo} alt="Logo" className="logo_img logo_img_tree" /> */}
              <img src={logo} alt="Logo" className="logo_img" />
            </Typography>
          </Box>

          <Box
            sx={{
              marginLeft: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {status === authStatus.AUTHENTICATED && (
              <List className="nav-list">
                {Object.keys(routes).map(route => {
                  if (route === 'settings' && user?.is_superuser) {
                    return (
                      <ListItem key={route}>
                        <Tooltip title={t(`pages.${route}.primary`)} arrow>
                          <Typography
                            noWrap
                            component={NavLink}
                            className="nav_link"
                            to={routes[route].primary}
                            // reloadDocument
                          >
                            <SettingsIcon />
                          </Typography>
                        </Tooltip>
                      </ListItem>
                    );
                  }

                  if (route === 'settings' && !user?.is_superuser) {
                    return null;
                  }

                  // --- USER ---
                  if (route === 'user' && user?.is_superuser) {
                    return null;
                  }

                  if (route === 'user') {
                    return (
                      <ListItem key={route}>
                        <Tooltip title={t(`pages.${route}.primary`)} arrow>
                          <Typography
                            noWrap
                            component={NavLink}
                            className="nav_link"
                            to={routes[route].primary}
                          >
                            <PermContactCalendarIcon />
                          </Typography>
                        </Tooltip>
                      </ListItem>
                    );
                  }

                  return (
                    <ListItem key={route}>
                      <Typography
                        noWrap
                        component={NavLink}
                        className="nav_link"
                        to={routes[route].primary}
                      >
                        {t(`pages.${route}.primary`)}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            )}

            <FormControl
              sx={{
                ml: { xs: 'auto', md: '10px' },
                width: '40px',
                height: '35px',
              }}
            >
              <Select
                id="Select-lang"
                name="Select-lang"
                value={currentLng}
                onChange={handleLngChange}
                autoWidth
                displayEmpty
                IconComponent={() => null}
                className="lang"
                sx={{ textAlign: 'center' }}
              >
                {languageOption.map(lng => (
                  <MenuItem key={lng} value={lng}>
                    {lng.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {status === authStatus.AUTHENTICATED && (
              <Box>
                <Tooltip title={t('promptbar:logout')} arrow>
                  <IconButton
                    sx={{ ml: '10px' }}
                    size="large"
                    color="inherit"
                    aria-haspopup="true"
                    aria-controls="logout"
                    aria-label="logout"
                    onClick={handleLogout}
                  >
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* аватар + меню с настройками пользователя */}

          {/* <Box sx={{ ml: '20px' }}>
            <Tooltip title="Guest User">
              <IconButton sx={{ p: 0 }} onClick={handleOpenUserMenu}>
                <Avatar
                  {...stringAvatar('Guest User')}
                  alt="Guest User"
                  sx={{ width: '45px', height: '45px' }}
                />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem>
                <Typography
                  textAlign="center"
                  component={Link}
                  to="settings"
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {t('promptbar:setting')}
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography textAlign="center">
                  {t('promptbar:logout')}
                </Typography>
              </MenuItem>
            </Menu>
          </Box> */}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
